/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Button, Tooltip } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadDeliveryMISReport } from "../../../../utils/downloadUtils";
import { useNavigate } from "react-router-dom";

const DeliveryMISReport = () => {
    const FILTER_STORAGE_KEY = "DELIVERY_MIS_REPORT_FILTERS";

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(1, "days"),
        moment()
    ]);
    const [hubList, setHubList] = useState([]);
    const [selectedHubs, setSelectedHubs] = useState([]);
    const [cpList, setCpList] = useState([]);
    const [selectedCps, setSelectedCps] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [hubUser, setHubUser] = useState();
    const [pendingCpSelection, setPendingCpSelection] = useState(null); // Store CP selection to restore after CP list loads
    const [headerHubList, setHeaderHubList] = useState([]);
    const [activeHubId, setActiveHubId] = useState(JSON.parse(localStorage.getItem("active_hub") || "{}")?.hubId || "ALL_HUBS");
    const hubInitializedRef = useRef(false);

    // Helper function to save filters to sessionStorage
    const saveFiltersToStorage = () => {
        const filters = {
            dateRange: dateRange ? [
                dateRange[0]?.format("YYYY-MM-DD"),
                dateRange[1]?.format("YYYY-MM-DD")
            ] : null,
            selectedHubs,
            selectedCps,
            pagination: {
                current: pagination.current,
                pageSize: pagination.pageSize
            }
        };
        sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    };

    // Fetch hubs from header/localStorage for dropdown options
    useEffect(() => {
        try {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            if (userHubs?.length > 0) {
                const headerHubs = [
                    { label: "All", value: "ALL" },
                    ...userHubs.map(hub => ({
                        label: hub.hubName,
                        value: hub.hubId
                    }))
                ];
                setHeaderHubList(headerHubs);
            } else {
                setHeaderHubList([]);
            }
        } catch (e) {
            console.error("Error parsing user hubs for Delivery MIS Report", e);
            setHeaderHubList([]);
        }
    }, []);

    const handleMultiSelectChange = (values, setter, options = []) => {
        if (values.includes("ALL")) {
            const isLatestAll = values[values.length - 1] === "ALL";
            if (isLatestAll) {
                // Select all actual values (excluding the "ALL" option itself)
                const allActualValues = options
                    .filter(opt => opt.value !== "ALL")
                    .map(opt => opt.value);
                setter(allActualValues);
            } else {
                // If it wasn't the latest click, just filter it out (though shouldn't happen with this logic)
                setter(values.filter(v => v !== "ALL"));
            }
        } else {
            setter(values);
        }
    };

    // Sync with App Header hub changes
    useEffect(() => {
        const handleHubChanged = () => {
            const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
            const newActiveHubId = activeHub?.hubId || "ALL_HUBS";
            setActiveHubId(newActiveHubId);
            if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
                setSelectedHubs([newActiveHubId]);
            } else {
                setSelectedHubs(["ALL"]);
            }
        };

        window.addEventListener("hubChanged", handleHubChanged);
        return () => window.removeEventListener("hubChanged", handleHubChanged);
    }, []);

    const keyMapping = {
        "Outscan By Hub": "outscanned",
        "Inscan By CP": "inscanned",
        "Not Inscan By CP": "notInScan",
        "DRS Generation": "drsGenerationByCPOfSameDay",
        "Undelivered": "undelivered",
        "RTO": "rto",
        "Same Day Deliveries": "delivered",
        "Total Delivery": "totalDelivery"
    };

    // Combined initial setup useEffect
    useEffect(() => {
        const initializeData = async () => {
            try {
                // setLoading(true);
                // Get user info first
                const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
                const userid = JSON.parse(localStorage.getItem("userId") || "{}");
                const userRole = user?.roles?.[0];
                setHubUser(userRole);

                // Fetch hub list
                const hubs = await getHubList();
                setHubList(hubs);

                // Restore filters
                const hasRestoredFilters = (function() {
                    try {
                        const savedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
                        if (savedFilters) {
                            const filters = JSON.parse(savedFilters);
                            if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
                                setDateRange([moment(filters.dateRange[0]), moment(filters.dateRange[1])]);
                            }
                            if (filters.selectedHubs && filters.selectedHubs.length > 0) {
                                setSelectedHubs(filters.selectedHubs);
                            }
                            if (filters.selectedCps && filters.selectedCps.length > 0) {
                                setPendingCpSelection(filters.selectedCps);
                            }
                            if (filters.pagination) {
                                setPagination(prev => ({
                                    ...prev,
                                    current: filters.pagination.current || prev.current,
                                    pageSize: filters.pagination.pageSize || prev.pageSize
                                }));
                            }
                            sessionStorage.removeItem(FILTER_STORAGE_KEY);
                            return true;
                        }
                    } catch (e) {
                        console.error("Error restoring filters", e);
                    }
                    return false;
                })();

                const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
                const currentActiveHubId = activeHub?.hubId || "ALL_HUBS";

                // Only set default hub if no filters were restored and not yet initialized
                if (!hasRestoredFilters && !hubInitializedRef.current) {
                    const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
                    const userHubs = userFullData?.userInfo?.hubDetails || [];
                    
                    if (currentActiveHubId && currentActiveHubId !== "ALL_HUBS") {
                        setSelectedHubs([currentActiveHubId]);
                    } else if (userHubs?.length === 0) {
                        setSelectedHubs([]);
                    } else {
                        setSelectedHubs(["ALL"]);
                    }
                    hubInitializedRef.current = true;
                }
            } catch (error) {
                message.error("Failed to fetch initial data");
            } finally {
                // setLoading(false);
            }
        };

        initializeData();
    }, [headerHubList]); // Depend on headerHubList
    // Fetch CP list when hubs change
    useEffect(() => {
        if (selectedHubs.length > 0) {
            Promise.all(selectedHubs.map(hubId => getHubCPList(hubId)))
                .then(cpLists => {
                    const allCps = [...new Set(cpLists.flat())];
                    setCpList(allCps);
                })
                .catch(() => {
                    message.error("Failed to fetch CP lists");
                    setCpList([]);
                });
        } else {
            setCpList([]);
            setSelectedCps([]);
            setPendingCpSelection(null); // Clear pending CP selection
        }
    }, [selectedHubs]);

    // Restore CP selection after CP list is loaded
    useEffect(() => {
        if (pendingCpSelection && cpList.length > 0) {
            // Filter to only include CPs that exist in the current CP list
            const validCpIds = pendingCpSelection.filter(cpId =>
                cpList.some(cp => cp.value === cpId)
            );
            if (validCpIds.length > 0) {
                setSelectedCps(validCpIds);
            }
            setPendingCpSelection(null); // Clear pending selection after restoring
        }
    }, [cpList, pendingCpSelection]);

    // Fetch report data when filters or pagination change
    useEffect(() => {
        // Wait for hubUser to be initialized
        if (!hubUser) {
            return;
        }
        if (selectedHubs.length === 0) {
            return;
        }

        // For HUBADMIN users, hub selection is mandatory
        if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedHubs.length === 0) {
            return; // Don't fetch if HUBADMIN has no hub selected
        }
        // If CPs are pending restoration or selected, wait for CP list to be loaded
        if ((pendingCpSelection || (selectedCps && selectedCps.length > 0)) && cpList.length === 0 && selectedHubs.length > 0) {
            return; // Wait for CP list to load
        }
        fetchReport();
        // eslint-disable-next-line
    }, [hubUser, selectedHubs, selectedCps, dateRange, pagination.current, pagination.pageSize, cpList, pendingCpSelection]);

    const fetchReport = async () => {
        setLoading(true);
        try {
            // For HUBADMIN users, hub selection is mandatory
            if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedHubs.length === 0) {
                setReportData([]);
                setPagination(prev => ({
                    ...prev,
                    total: 0
                }));
                return;
            }

            const activeOptions = headerHubList?.length > 0 ? headerHubList : hubList;
            const resolvedHubIds = selectedHubs?.includes("ALL")
                ? activeOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedHubs;

            const selectedCpData = cpList.filter(cp => selectedCps?.includes(cp.value));


            const params = {
                page: pagination.current,
                limit: pagination.pageSize,
                ...(dateRange[0] && { startDate: dateRange[0].toISOString().split("T")[0] }),
                ...(dateRange[1] && { endDate: dateRange[1].toISOString().split("T")[0] }),
                ...(resolvedHubIds?.length > 0 && { hubId: resolvedHubIds.join(",") }),
                ...(selectedCpData?.length > 0 && { cpId: selectedCpData.map(cp => cp.value).join(",") })
            };

            const response = await ReportsService.getDeliveryMISReport(params);

            if (response?.data) {
                setReportData(Array.isArray(response.data) ? response.data : []);
                setLoading(false);
                setPagination(prev => ({
                    ...prev,
                    total: response.total || 0,
                }));
            } else {
                setReportData([]);
                setPagination(prev => ({
                    ...prev,
                    total: 0
                }));
                message.error("No data available");
            }
        } catch (error) {
            message.error(error?.message || "Failed to fetch report data");
            setReportData([]);
            setPagination(prev => ({
                ...prev,
                total: 0
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const handleNumberClick = (type, value, record) => {
        if (!value) return;

        // Save current filters before navigating
        saveFiltersToStorage();

        const key = keyMapping[type];
        if (!key) {
            return;
        }

        // Navigate to details page with query parameters
        navigate("/delivery-details", {
            state: {
                date: record.date,
                cpId: record?.cpId,
                hubId: record?.hubId,
                key: key,
                type: type,
                hubName: record?.hubName,
                cpName: record?.cpName
            }
        });
    };

    const columns = [
        {
            title: "No.",
            dataIndex: "index",
            key: "index",
            width: 60,
            render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            sorter: true,
            width: 100
        },
        {
            title: "Hub Name",
            dataIndex: "hubName",
            key: "hubName",
            sorter: true,
            width: 160
        },
        {
            title: "CP Name",
            dataIndex: "cpName",
            key: "cpName",
            sorter: true,
            width: 160
        },
        {
            title: "Outscan By Hub",
            dataIndex: "outscanByHub",
            key: "outscanByHub",
            sorter: true,
            width: 130,
            align: "right",
            render: (value, record) => renderClickableNumber(value, "Outscan By Hub", record)
        },
        {
            title: "Not Inscan By CP",
            dataIndex: "notInScanByCP",
            key: "notInScanByCP",
            sorter: true,
            width: 140,
            align: "right",
            render: (value, record) => renderClickableNumber(value, "Not Inscan By CP", record)
        },
        {
            title: "Inscan By CP",
            dataIndex: "inscanByCP",
            key: "inscanByCP",
            sorter: true,
            width: 120,
            align: "right",
            render: (value, record) => renderClickableNumber(value, "Inscan By CP", record)
        },
        {
            title: "DRS Generation By CP (Same Day)",
            dataIndex: "drsGenerationByCPOfSameDay",
            key: "drsGenerationByCPOfSameDay",
            sorter: true,
            width: 220,
            align: "right",
            render: (value, record) => renderClickableNumber(value, "DRS Generation", record)
        },
        {
            title: "Same Day Deliveries",
            dataIndex: "sameDayDeliveries",
            key: "sameDayDeliveries",
            sorter: true,
            width: 150,
            align: "right",
            render: (value, record) => renderClickableNumber(value, "Same Day Deliveries", record)
        },
        {
            title: "Undelivered",
            dataIndex: "undelivered",
            key: "undelivered",
            sorter: true,
            width: 110,
            align: "right",
            render: (value, record) => renderClickableNumber(value, "Undelivered", record)
        },
        {
            title: "RTO",
            dataIndex: "rto",
            key: "rto",
            sorter: true,
            width: 90,
            align: "right",
            render: (value, record) => renderClickableNumber(value, "RTO", record)
        },
        {
            title: "Total Delivery",
            dataIndex: "totalDelivery",
            key: "totalDelivery",
            sorter: true,
            width: 130,
            align: "right",
            render: (value, record) => renderClickableNumber(value, "Total Delivery", record)
        }
    ];

    const renderClickableNumber = (value, type, record) => {
        if (!value) return value;
        return (
            <Tooltip title={`Click to view ${type} details`}>
                <a
                    onClick={() => handleNumberClick(type, value, record)}
                    style={{
                        color: "#1890ff",
                        cursor: "pointer",
                        display: "block",
                        textAlign: "right"
                    }}
                >
                    {value.toLocaleString()}
                </a>
            </Tooltip>
        );
    };

    // Handler functions for filters
    const handleDateChange = (dates) => {
        setDateRange(dates);
    };

    const handleHubChange = (value) => {
        setSelectedHubs(value);
        if (value?.length > 0) {
            // Fetch CP list when hubs change
            Promise.all(value.map(hubId => getHubCPList(hubId)))
                .then(cpLists => {
                    const allCps = [...new Set(cpLists.flat())];
                    setCpList(allCps);
                })
                .catch(() => {
                    message.error("Failed to fetch CP lists");
                    setCpList([]);
                });
        } else {
            setCpList([]);
        }
    };

    const handleCpChange = (value) => {
        setSelectedCps(value);
    };

    const handleDownload = async () => {
        if (!dateRange[0] || !dateRange[1]) {
            message.error("Please select a date range for download");
            return;
        }

        if (selectedHubs.length === 0) {
            message.error("Please select at least one hub for download");
            return;
        }

        setDownloadLoading(true);
        try {
            const activeOptions = headerHubList?.length > 0 ? headerHubList : hubList;
            const resolvedHubIds = selectedHubs?.includes("ALL")
                ? activeOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedHubs;

            const selectedCpData = cpList.filter(cp => selectedCps?.includes(cp.value));

            const params = {
                startDate: dateRange[0].toISOString().split("T")[0],
                endDate: dateRange[1].toISOString().split("T")[0],
                ...(resolvedHubIds?.length > 0 && { hubId: resolvedHubIds.join(",") }),
                ...(selectedCpData?.length > 0 && { cpId: selectedCpData.map(cp => cp.value).join(",") })
            };

            await downloadDeliveryMISReport(params);
            message.success("Delivery MIS Report downloaded successfully");
        } catch (error) {
            console.error("Download error:", error);
            message.error(error?.message || "Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading title="Delivery MIS Report" />
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={24} md={24} lg={24} style={{ textAlign: "right" }}>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Date Range</div>
                        <CalendarComponent
                            className="custom-calendar"
                            type="range"
                            value={dateRange}
                            onChange={handleDateChange}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Hub"
                            options={headerHubList?.length > 0 ? headerHubList : hubList}
                            onChange={(val) => {
                                const baseOptions = headerHubList?.length > 0 ? headerHubList : hubList;
                                handleMultiSelectChange(val, setSelectedHubs, baseOptions);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedHubs}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">CP</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="CP"
                            options={cpList}
                            onChange={handleCpChange}
                            value={selectedCps}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} style={{ textAlign: "right", marginTop: 24 }}>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            loading={downloadLoading}
                            onClick={handleDownload}
                            disabled={!dateRange[0] || !dateRange[1] || selectedHubs.length === 0}
                        >
                            Download Report
                        </Button>
                    </Col>

                </Row>

            </Card>
            <TableComponent
                columns={columns}
                dataSource={reportData}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ y: "calc(100vh - 300px)", x: "max-content" }}
                sticky
            />
        </div>
    );
};

export default DeliveryMISReport; 
